import React from "react";
import { GridApiPro, GridRowId, GridRowModelReplace, GridRowModelUpdate } from "@mui/x-data-grid-pro";
import type { AavoDataGridProps } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import {
	InlineCrudGridGetRowId,
	InlineCrudGridRowData,
} from "src/components/common/dataGrid/crud/useInlineCrudGridRows.ts";

type ProcessRowUpdate<RowData extends object> = AavoDataGridProps<RowData>["processRowUpdate"];

interface PendingRowUpdateWaiter {
	rowId: GridRowId;
	versionBefore: number;
	resolve: () => void;
}

/**
 * Tracks committed inline edit changes separately from MUI grid state.
 *
 * MUI commits an active editor through `processRowUpdate`, but clicking an external Save button can happen before
 * that callback has run. This hook makes `processRowUpdate` the canonical place where changed rows are captured,
 * and gives Save an explicit way to ask MUI to stop editing before reading those captured rows.
 */
export function useInlineCrudGridChanges<RowData extends object>({
	changedRowIds,
	getGridApi,
	getRowId,
	localRows,
	processRowUpdate,
	rowUnderEdit,
	updateLocalRows,
}: {
	changedRowIds: GridRowId[];
	getGridApi: () => GridApiPro;
	getRowId: InlineCrudGridGetRowId<RowData>;
	localRows: InlineCrudGridRowData<RowData>[];
	processRowUpdate?: ProcessRowUpdate<RowData>;
	rowUnderEdit: GridRowId | undefined;
	updateLocalRows: (updates: GridRowModelUpdate[]) => void;
}) {
	const changedRowsRef = React.useRef(new Map<GridRowId, RowData>());
	const rowUpdateVersionRef = React.useRef(0);
	const pendingRowUpdateWaiterRef = React.useRef<PendingRowUpdateWaiter | null>(null);

	const handleProcessRowUpdate: NonNullable<ProcessRowUpdate<RowData>> = async (newRow, oldRow, params) => {
		const processResult = processRowUpdate == null ? newRow : await processRowUpdate(newRow, oldRow, params);
		const processedRow = isGridRowModelReplace(processResult) ? processResult.row : processResult;
		updateLocalRows([processedRow as GridRowModelUpdate]);
		const processedRowId = getRowId(processedRow);
		// Save reads this buffer instead of scraping MUI's transient edit state.
		changedRowsRef.current.set(processedRowId, processedRow);
		rowUpdateVersionRef.current += 1;
		resolvePendingRowUpdateWaiter(processedRowId);
		return processResult;
	};

	async function waitForEditedRowToCommit() {
		if (rowUnderEdit === undefined) return;

		const gridApi = getGridApi();
		const versionBefore = rowUpdateVersionRef.current;
		const rowMode = gridApi.getRowMode(rowUnderEdit);

		if (rowMode === "edit") {
			gridApi.stopRowEditMode({ id: rowUnderEdit });
		}

		// `stopRowEditMode` is not awaitable, so wait until our `processRowUpdate` wrapper observes the commit.
		await waitForRowUpdateVersion(rowUnderEdit, versionBefore);
	}

	function getChangedRows(): RowData[] {
		return changedRowIds
			.map((rowId) => getChangedRow(rowId))
			.filter((row) => row !== null && row !== undefined);
	}

	function clearChangedRows(changedRows: RowData[]) {
		changedRows.forEach((row) => {
			changedRowsRef.current.delete(getRowId(row));
		});
	}

	function clearChangedRowsByIds(rowIds: GridRowId[]) {
		rowIds.forEach((rowId) => changedRowsRef.current.delete(rowId));
	}

	function resetChanges() {
		changedRowsRef.current.clear();
	}

	function getChangedRow(rowId: GridRowId): RowData | undefined {
		const changedRow = changedRowsRef.current.get(rowId);
		if (changedRow != null) return changedRow;

		return localRows.find((row) => getRowId(row) === rowId) as RowData | undefined;
	}

	function waitForRowUpdateVersion(rowId: GridRowId, versionBefore: number) {
		if (rowUpdateVersionRef.current !== versionBefore) return Promise.resolve();

		return new Promise<void>((resolve) => {
			// Do not let Save hang forever if MUI does not call `processRowUpdate` for this edit stop.
			const timeout = window.setTimeout(() => {
				complete();
			}, 1000);

			pendingRowUpdateWaiterRef.current = {
				rowId,
				versionBefore,
				resolve: complete,
			};

			function complete() {
				window.clearTimeout(timeout);
				if (pendingRowUpdateWaiterRef.current?.resolve === complete) {
					pendingRowUpdateWaiterRef.current = null;
				}
				resolve();
			}
		});
	}

	function resolvePendingRowUpdateWaiter(rowId: GridRowId) {
		const waiter = pendingRowUpdateWaiterRef.current;
		if (waiter == null) return;
		if (waiter.rowId !== rowId) return;
		if (rowUpdateVersionRef.current === waiter.versionBefore) return;

		waiter.resolve();
	}

	return {
		handleProcessRowUpdate,
		waitForEditedRowToCommit,
		getChangedRows,
		clearChangedRows,
		clearChangedRowsByIds,
		resetChanges,
	};
}

function isGridRowModelReplace<RowData extends object>(
	row: RowData | GridRowModelReplace<RowData>,
): row is GridRowModelReplace<RowData> {
	return "_action" in row && row._action === "replace";
}
